/**
 * Split job-log text into plain runs and clickable 1Password links.
 *
 * Only the 1Password item opener (`https://start.1password.com/open/i`) is
 * linked — the deep link sm4rt-api writes when a secure credential arrives.
 * Everything else stays plain text on purpose: RS comments can carry text a
 * customer wrote (an email reply lands as a comment), so a general linkifier
 * would turn the job log into a place to click a stranger's URL.
 *
 * The link opens the item in the technician's own 1Password session; it is a
 * reference, not a credential.
 */
export type LogTextSegment =
  | { kind: 'text'; value: string }
  | { kind: 'link'; href: string; label: string };

export const ONEPASSWORD_LINK_LABEL = 'Open in 1Password';

const URL_PATTERN = /https:\/\/\S+/g;

function isOnePasswordItemLink(candidate: string): boolean {
  try {
    const url = new URL(candidate);
    return (
      url.protocol === 'https:' &&
      url.hostname === 'start.1password.com' &&
      url.pathname === '/open/i' &&
      url.searchParams.has('i') &&
      url.searchParams.has('v')
    );
  } catch {
    return false;
  }
}

export function splitLogText(text: string): LogTextSegment[] {
  const segments: LogTextSegment[] = [];
  let cursor = 0;
  for (const match of text.matchAll(URL_PATTERN)) {
    const href = match[0];
    const start = match.index ?? 0;
    if (!isOnePasswordItemLink(href)) continue;
    if (start > cursor) segments.push({ kind: 'text', value: text.slice(cursor, start) });
    segments.push({ kind: 'link', href, label: ONEPASSWORD_LINK_LABEL });
    cursor = start + href.length;
  }
  if (cursor < text.length) segments.push({ kind: 'text', value: text.slice(cursor) });
  return segments;
}
